import Debug from 'debug';
import { Dispatch, Middleware } from 'redux';
import { fetchApi } from './fetchApi';
import {
  addQueryToGCache,
  addRecordToGCache,
  GCacheState,
  GCacheStore,
  cacheSweep,
  clearAllQueriesForEntityInGCache,
  clearSingleQueryInGCache,
  clearSingleRecordInGCache,
  getQueryFromGCache,
  GetQueryPayload,
  getRecordFromGCache,
  GetRecordPayload,
  getSingleRetrievalQuery,
  makeQueryGCacheKey,
  makeRecordGCacheKey,
  setQueryDataInGCache,
  setQueryErrorInGCache,
  setRecordDataInGCache,
  setRecordErrorInGCache,
  setAllFetchingRecordsAsNotFound,
} from './ReduxGCache';
import { DataResponse, DataVolume, doFrontEndEvaluates, Entity, getRowKey, Row } from 'gossamer-unified';

const debug = Debug('gfe/da/ReduxCacheMiddleware');

const shouldRetrieveAllDataInSingleQuery = (entity: Entity<Row>) => entity.dataVolume === DataVolume.Small;

const getTimeToLiveRecordMillis = (entity: Entity<Row>) => 2 * 60 * 60 * 1000;

const getTimeToLiveQueryMillis = (entity: Entity<Row>) => 1 * 60 * 60 * 1000;

// QueryResponse should never error with not found....
export const isNotFound = (record: DataResponse) =>
  typeof record.error === 'string' && record.error.toLowerCase().indexOf('not found') > -1;

export const makeResetCache =
  (dispatch: Dispatch) =>
  (entityId: string, updateKey?: string): void => {
    dispatch(
      clearAllQueriesForEntityInGCache({
        entityId,
      }),
    );
    if (updateKey) {
      dispatch(
        clearSingleRecordInGCache({
          entityId,
          key: updateKey,
        }),
      );
    }
  };

// Middleware
export const gCacheMiddleware: Middleware<{}, { cache: GCacheState }> = ((store: GCacheStore) =>
  (next: Dispatch) =>
  (action: { type: string; payload: any }) => {
    debug(`inside cacheMiddleware action`, action.type, action.payload);
    if (action.type === getRecordFromGCache.type) {
      onGetRecordFromGCache(store.getState().cache, next, action.payload);
    } else if (action.type === getQueryFromGCache.type) {
      onGetQueryFromGCache(store.getState().cache, next, action.payload);
    } else if (action.type === cacheSweep.type) {
      doGCacheSweep(store.getState().cache, next);
    } else {
      next(action);
    }
  }) as Middleware<{}, { cache: GCacheState }>;

const onGetRecordFromGCache = (cache: GCacheState, next: Dispatch, payload: GetRecordPayload) => {
  const now = Date.now();
  const item = cache.records[makeRecordGCacheKey(payload.entity.id, payload.key)];
  const cacheHit = !!item && item.expiresAt > now;
  debug(
    `onGetRecordFromGCache ${payload.entity.id}/${payload.key} from cache: ${cacheHit}, ${item?.expiresAt} >? ${now}`,
  );
  if (!cacheHit) {
    onRecordGCacheMiss(cache, next, payload);
  }
};

const onRecordGCacheMiss = (cache: GCacheState, next: Dispatch, payload: GetRecordPayload) => {
  next(
    addRecordToGCache({
      entityId: payload.entity.id,
      key: payload.key,
    }),
  );
  if (shouldRetrieveAllDataInSingleQuery(payload.entity)) {
    onGetQueryFromGCache(cache, next, {
      entity: payload.entity,
      query: getSingleRetrievalQuery(),
    });
    return;
  }
  const path = `/${payload.entity.service}/${payload.entity.id}/${encodeURIComponent(payload.key)}`;
  debug(`calling fetchApi`, path);
  fetchApi('GET', path)
    .then((data) => {
      debug(`return from fetchApi ${payload.entity.id}/${payload.key} with data: ${data}, ${typeof data}`);
      if (typeof data === 'string') {
        next(
          setRecordErrorInGCache({
            entityId: payload.entity.id,
            key: payload.key,
            error: data,
          }),
        );
      } else {
        next(
          setRecordDataInGCache({
            entityId: payload.entity.id,
            key: payload.key,
            data: doFrontEndEvaluates(payload.entity, data),
            expiresAt: Date.now() + getTimeToLiveRecordMillis(payload.entity),
          }),
        );
      }
    })
    .catch((err) => {
      next(
        setRecordErrorInGCache({
          entityId: payload.entity.id,
          key: payload.key,
          error: err.message,
        }),
      );
    });
};

const onGetQueryFromGCache = (cache: GCacheState, next: Dispatch, payload: GetQueryPayload) => {
  const now = Date.now();
  const queryStr = JSON.stringify(payload.query);
  const item = cache.queries[makeQueryGCacheKey(payload.entity.id, queryStr)];
  const cacheHit = !!item && item.expiresAt > now;
  debug(`onGetQueryFromGCache ${payload.entity.id}/{...} from cache: ${cacheHit}, ${item?.expiresAt} >? ${now}`);
  if (cacheHit) {
    debug(`data already exists: ${cache.queries[makeQueryGCacheKey(payload.entity.id, queryStr)]?.data?.length}`);
  }
  if (!cacheHit) {
    onQueryGCacheMiss(next, payload, queryStr);
  }
};

const onQueryGCacheMiss = (next: Dispatch, payload: GetQueryPayload, queryStr: string) => {
  next(
    addQueryToGCache({
      entityId: payload.entity.id,
      query: queryStr,
    }),
  );
  const path = `/${payload.entity.service}/${payload.entity.id}?q=${encodeURIComponent(queryStr)}`;
  debug(`calling fetchApi`, payload.entity.id, queryStr);
  fetchApi('GET', path)
    .then((data) => {
      debug(`return from fetchApi for ${payload.entity.id} has ${Array.isArray(data) && data.length} data records`);
      if (typeof data === 'string') {
        next(
          setQueryErrorInGCache({
            entityId: payload.entity.id,
            query: queryStr,
            error: data,
          }),
        );
      } else {
        const localData = (data?.data || []).map((row: Row) => doFrontEndEvaluates(payload.entity, row));
        next(
          setQueryDataInGCache({
            entityId: payload.entity.id,
            query: queryStr,
            data: localData,
            keys: localData.map((row: Row) => getRowKey(payload.entity, row)),
            expiresAt: Date.now() + getTimeToLiveQueryMillis(payload.entity),
          }),
        );
      }
      // a universal query gets all records, so any other requests must be not found
      if (queryStr === JSON.stringify(getSingleRetrievalQuery())) {
        next(
          setAllFetchingRecordsAsNotFound({
            entityId: payload.entity.id,
          }),
        );
      }
    })
    .catch((err) => {
      next(
        setQueryErrorInGCache({
          entityId: payload.entity.id,
          query: queryStr,
          error: err.message,
        }),
      );
    });
};

/**
 * This approach will remove cached items even if still in use in the page. This
 * causes an "infinite spinner" as nothing triggers a reload of these items.
 */
const doGCacheSweep = (cache: GCacheState, next: Dispatch) => {
  debug(`starting cache sweep... queries: ${Object.values(cache.queries).length}`);
  let count = 0;
  const now = Date.now();
  Object.values(cache.queries).forEach((item) => {
    debug(`checking item ${item.expiresAt} < ${now} ?`);
    if (item.expiresAt < now) {
      debug(`yes! removing...`);
      count += 1;
      next(
        clearSingleQueryInGCache({
          entityId: item.entityId,
          query: item.query,
        }),
      );
    }
  });
  Object.values(cache.records).forEach((item) => {
    debug(`checking item ${item.expiresAt} < ${now} ?`);
    if (item.expiresAt < now) {
      debug(`yes! removing...`);
      count += 1;
      next(
        clearSingleRecordInGCache({
          entityId: item.entityId,
          key: item.key,
        }),
      );
    }
  });
  debug(`finishing cache sweep... ${count}`);
};
